import { AICycleContent } from "../../types/ai-templates";
import { motion } from "framer-motion";
import { useState } from "react";
import NetworkIcon from "@core/network-icon";

interface CycleTemplate4Props {
  content: AICycleContent;
  onUpdate: (newContent: AICycleContent) => void;
}

// Animation variants (kept for container fade-in, no SVG animations)
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
};

export function CycleTemplate3({ content, onUpdate }: CycleTemplate4Props) {
  const [editingStep, setEditingStep] = useState<number | null>(null);
  const [editingField, setEditingField] = useState<
    "title" | "description" | null
  >(null);
  const [tempValue, setTempValue] = useState<string>("");
  console.log(content);

  // Handle step update
  const handleStepUpdate = (
    stepIndex: number,
    field: "title" | "description",
    newText: string,
  ) => {
    const newContent = JSON.parse(JSON.stringify(content));
    if (newContent.steps && newContent.steps[stepIndex]) {
      newContent.steps[stepIndex][field] = newText;
      onUpdate(newContent);
    }
  };

  // Handle title update
  const handleTitleUpdate = (newTitle: string) => {
    const newContent = JSON.parse(JSON.stringify(content));
    newContent.title = newTitle;
    onUpdate(newContent);
  };

  // Handle click to edit
  const startEditing = (
    field: "title" | "description",
    stepIndex: number,
    value: string,
  ) => {
    setEditingField(field);
    setEditingStep(stepIndex);
    setTempValue(value);
  };

  return (
    <motion.div
      className="p-6 bg-base-200 rounded-lg shadow-md overflow-hidden"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <motion.div
        className="relative w-max h-full mx-auto"
        variants={containerVariants}
      >
        <svg
          width="768"
          height="492"
          viewBox="0 0 768 492"
          style={{
            fill: "none",
            stroke: "none",
            fillRule: "evenodd",
            clipRule: "evenodd",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            strokeMiterlimit: 1.5,
          }}
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
        >
          <style
            className="text-font-style fontImports"
            data-font-family="Roboto"
          >
            @import
            url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=block');
          </style>

          {/* Patterns */}
          <defs>
            <pattern
              id="crawlingPattern-5a87971a"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#5a87971a"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-65927c1a"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#65927c1a"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-818f641a"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#818f641a"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-0000001b"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#0000001b"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-00a3d9d9"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#00a3d9d9"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-00b15c75"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#00b15c75"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-5c87005e"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#5c87005e"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-5f5f5f1a"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#5f5f5f1a"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <symbol id="crawlingSymbol">
              <g>
                <path d="M -12.5 12.5 l 13 -13 l 6 0 l -13 13 Z M -0.5 12.5 l 13 -13 l 6 0 l -13 13 Z M 11.5 12.5 l 13 -13 l 6 0 l -13 13 Z"></path>
              </g>
            </symbol>
          </defs>

          {/* Step 1 Path */}
          <g
            id="g-root-cp_1_g-1_13z9lu4v4x72b-stroke-fill"
            data-item-order="-257786"
            transform="translate(51.5, 134)"
          >
            <g id="cp_1_g-1_13z9lu4v4x72b-fill" stroke="none" fill="#344e57">
              <g>
                <path d="M 438 82L 438 10L 461.1093 10C 466.8307 10 470.7026 15.8314 468.4824 21.1045L 444.7211 77.5373C 443.5825 80.2415 440.9341 82 438 82ZM 438.000008 82L 438.000008 10L 134 10L 134 82L 438.000008 82ZM 133.998863 82L 133.626463 82C 130.407663 82 127.502363 80.071 126.253363 77.1045L 102.674363 21.1045C 100.454163 15.8314 104.326062 10 110.047463 10L 133.998863 10L 133.998863 82Z"></path>
              </g>
            </g>

            <g
              id="cp_1_g-1_13z9lu4v4x72b-stroke"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
              stroke={content.steps[0].color || "#1ac3fb"}
              strokeWidth="2"
            >
              <g>
                <path d="M 438 82C 440.9341 82 443.5825 80.2415 444.7211 77.5373L 468.4824 21.1045C 470.7026 15.8314 466.8307 10 461.1093 10L 438 10M 133.974091 10L 438.001297 10M 438.001297 82L 133.974091 82L 133.628906 82M 133.998863 82L 133.626463 82C 130.407663 82 127.502363 80.071 126.253363 77.1045L 102.674363 21.1045C 100.454163 15.8314 104.326062 10 110.047463 10L 133.998863 10"></path>
              </g>
            </g>
            <g id="cp_1_g-1_13z9lu4v4x72b-fill" stroke="none" fill="#344e57">
              <g>
                <path d="M 10 46C 10 65.882251 26.117749 82 46 82C 65.882251 82 82 65.882251 82 46C 82 26.117749 65.882251 10 46 10C 26.117749 10 10 26.117749 10 46"></path>
              </g>
            </g>

            <g
              id="cp_1_g-1_13z9lu4v4x72b-stroke"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
              stroke={content.steps[0].color || "#1ac3fb"}
              strokeWidth="2"
            >
              <g>
                <path d="M 10 46C 10 65.882251 26.117749 82 46 82C 65.882251 82 82 65.882251 82 46C 82 26.117749 65.882251 10 46 10C 26.117749 10 10 26.117749 10 46"></path>
              </g>
            </g>
          </g>

          {/* Step 2 Path */}
          <g
            id="g-root-cp_2_g-2_v3mll8v4x7v1-stroke-fill"
            data-item-order="-257756"
            transform="translate(92.5, 230)"
          >
            <g id="cp_2_g-2_v3mll8v4x7v1-fill" stroke="none" fill="#3b5649">
              <g>
                <path d="M 126.253363 77.1045L 102.674363 21.1045C 100.454163 15.8314 104.326063 10 110.047463 10L 132.998063 10L 132.998063 82C 129.779263 82 127.502463 80.071 126.253363 77.1045ZM 356.999985 82L 132.999985 82L 132.999985 10L 356.999985 10L 356.999985 82ZM 364.0621 77.1045L 387.6411 21.1045C 389.8613 15.8314 385.9894 10 380.2679 10L 357 10L 357 82C 360.2187 82 362.813 80.071 364.0621 77.1045Z"></path>
              </g>
            </g>

            <g
              id="cp_2_g-2_v3mll8v4x7v1-stroke"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
              stroke={content.steps[1].color || "#43dd93"}
              strokeWidth="2"
            >
              <g>
                <path d="M 132.998063 82C 129.779263 82 127.502463 80.071 126.253363 77.1045L 102.674363 21.1045C 100.454163 15.8314 104.326063 10 110.047463 10L 132.998063 10M 356.999985 82L 132.999985 82M 356.999985 10L 132.999985 10M 357 82C 360.2187 82 362.813 80.071 364.0621 77.1045L 387.6411 21.1045C 389.8613 15.8314 385.9894 10 380.2679 10L 357 10"></path>
              </g>
            </g>
            <g id="cp_2_g-2_v3mll8v4x7v1-fill" stroke="none" fill="#3b5649">
              <g>
                <path d="M 10 46C 10 65.882251 26.117749 82 46 82C 65.882251 82 82 65.882251 82 46C 82 26.117749 65.882251 10 46 10C 26.117749 10 10 26.117749 10 46"></path>
              </g>
            </g>

            <g
              id="cp_2_g-2_v3mll8v4x7v1-stroke"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
              stroke={content.steps[1].color || "#43dd93"}
              strokeWidth="2"
            >
              <g>
                <path d="M 10 46C 10 65.882251 26.117749 82 46 82C 65.882251 82 82 65.882251 82 46C 82 26.117749 65.882251 10 46 10C 26.117749 10 10 26.117749 10 46"></path>
              </g>
            </g>
          </g>

          {/* Step 3 Path */}
          <g
            id="g-root-cp_3_g-3_qnt3gsv4x8nb-stroke-fill"
            data-item-order="-257728"
            transform="translate(132.5, 326)"
          >
            <g id="cp_3_g-3_qnt3gsv4x8nb-fill" stroke="none" fill="#4b533a">
              <g>
                <path d="M 277 82L 277 10L 299.425 10C 305.1465 10 309.0184 15.8314 306.7981 21.1044L 282.7714 78.1679C 281.7937 80.49 279.5195 82 277 82ZM 276.999998 82L 276.999998 10L 133 10L 133 82L 276.999998 82ZM 132.998863 82L 132.998863 10L 110.047463 10C 104.326063 10 100.454163 15.8314 102.674363 21.1045L 126.496862 77.6829C 127.598363 80.2989 130.160363 82 132.998863 82Z"></path>
              </g>
            </g>

            <g
              id="cp_3_g-3_qnt3gsv4x8nb-stroke"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
              stroke={content.steps[2].color || "#a6da37"}
              strokeWidth="2"
            >
              <g>
                <path d="M 277 82C 279.5195 82 281.7937 80.49 282.7714 78.1679L 306.7981 21.1044C 309.0184 15.8314 305.1465 10 299.425 10L 277 10M 276.999998 82L 133 82M 276.999998 10L 133 10M 132.998863 82C 130.160363 82 127.598363 80.2989 126.496862 77.6829L 102.674363 21.1045C 100.454163 15.8314 104.326063 10 110.047463 10L 132.998863 10"></path>
              </g>
            </g>
            <g id="cp_3_g-3_qnt3gsv4x8nb-fill" stroke="none" fill="#4b533a">
              <g>
                <path d="M 10 46C 10 65.882251 26.117749 82 46 82C 65.882251 82 82 65.882251 82 46C 82 26.117749 65.882251 10 46 10C 26.117749 10 10 26.117749 10 46"></path>
              </g>
            </g>

            <g
              id="cp_3_g-3_qnt3gsv4x8nb-stroke"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
              stroke={content.steps[2].color || "#a6da37"}
              strokeWidth="2"
            >
              <g>
                <path d="M 10 46C 10 65.882251 26.117749 82 46 82C 65.882251 82 82 65.882251 82 46C 82 26.117749 65.882251 10 46 10C 26.117749 10 10 26.117749 10 46"></path>
              </g>
            </g>
          </g>

          {/* Main Title */}
          <g
            id="g-root-tx_achievin_m7zlccv4x9fl-stroke-fill"
            data-item-order="0"
            transform="translate(158, 50)"
          >
            <g id="tx_achievin_m7zlccv4x9fl-fill" stroke="none" fill="#171717">
              <foreignObject x="13.56" y="20" width="400" height="30">
                <div
                  style={{
                    font: "bold 25px Roboto, sans-serif",
                    color: "#171717",
                    whiteSpace: "pre-wrap",
                    cursor: "pointer",
                    display:
                      editingField === "title" && editingStep === -1
                        ? "none"
                        : "block",
                  }}
                  onClick={() => startEditing("title", -1, content.title)}
                >
                  {content.title}
                </div>
                {editingField === "title" && editingStep === -1 && (
                  <input
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingStep(null);
                      if (tempValue !== content.title) {
                        handleTitleUpdate(tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") e.currentTarget.blur();
                    }}
                    className="px-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary"
                    style={{ font: "bold 25px Roboto, sans-serif" }}
                  />
                )}
              </foreignObject>
            </g>
          </g>

          {/* Step 1 Title and Description */}
          <g
            id="g-root-tx_identify_dcz26kv4x72i-stroke-fill"
            data-item-order="0"
            transform="translate(237.5, 140)"
          >
            <g
              id="tx_identify_dcz26kv4x72i-fill"
              stroke="none"
              fill={content.steps[0].color || "#1ac3fb"}
            >
              <foreignObject x="13.25" y="10" width="200" height="80">
                <div
                  className="text-center"
                  style={{
                    font: "bold 16px Roboto, sans-serif",
                    color: content.steps[0].color || "#1ac3fb",
                    whiteSpace: "pre-wrap",
                    cursor: "pointer",
                    display:
                      editingField === "title" && editingStep === 0
                        ? "none"
                        : "block",
                  }}
                  onClick={() =>
                    startEditing("title", 0, content.steps[0].title)
                  }
                >
                  {content.steps[0].title}
                </div>
                {editingField === "title" && editingStep === 0 && (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingStep(null);
                      if (tempValue !== content.steps[0].title) {
                        handleStepUpdate(0, "title", tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    className="px-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary"
                    style={{ font: "bold 16px Roboto, sans-serif" }}
                  />
                )}
              </foreignObject>
            </g>
          </g>
          <g
            id="g-root-tx_recogniz_1hi7t8sv4yner-stroke-fill"
            data-item-order="0"
            transform="translate(237.5, 164)"
          >
            <g id="tx_recogniz_1hi7t8sv4yner-fill" stroke="none" fill="#f4f4f4">
              <foreignObject x="12" y="10" width="200" height="60">
                <div
                  className="text-center"
                  style={{
                    font: "10px Roboto, sans-serif",
                    color: "#f4f4f4",
                    whiteSpace: "pre-wrap",
                    overflowWrap: "break-word",
                    maxWidth: "200px",
                    cursor: "pointer",
                    display:
                      editingField === "description" && editingStep === 0
                        ? "none"
                        : "block",
                  }}
                  onClick={() =>
                    startEditing("description", 0, content.steps[0].description)
                  }
                >
                  {content.steps[0].description}
                </div>
                {editingField === "description" && editingStep === 0 && (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingStep(null);
                      if (tempValue !== content.steps[0].description) {
                        handleStepUpdate(0, "description", tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    className="px-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary"
                    style={{
                      font: "10px Roboto, sans-serif",
                      maxWidth: "200px",
                    }}
                  />
                )}
              </foreignObject>
            </g>
          </g>

          {/* Step 2 Title and Description */}
          <g
            id="g-root-tx_implemen_147drykv4yltw-stroke-fill"
            data-item-order="0"
            transform="translate(231.5, 235)"
          >
            <g
              id="tx_implemen_147drykv4yltw-fill"
              stroke="none"
              fill={content.steps[1].color || "#43dd93"}
            >
              <foreignObject x="12.72" y="10" width="200" height="80">
                <div
                  className="text-center"
                  style={{
                    font: "bold 16px Roboto, sans-serif",
                    color: content.steps[1].color || "#43dd93",
                    whiteSpace: "pre-wrap",
                    cursor: "pointer",
                    display:
                      editingField === "title" && editingStep === 1
                        ? "none"
                        : "block",
                  }}
                  onClick={() =>
                    startEditing("title", 1, content.steps[1].title)
                  }
                >
                  {content.steps[1].title}
                </div>
                {editingField === "title" && editingStep === 1 && (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingStep(null);
                      if (tempValue !== content.steps[1].title) {
                        handleStepUpdate(1, "title", tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    className="px-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary"
                    style={{ font: "bold 16px Roboto, sans-serif" }}
                  />
                )}
              </foreignObject>
            </g>
          </g>
          <g
            id="g-root-tx_applying_908ukv51hc7-stroke-fill"
            data-item-order="0"
            transform="translate(231, 256)"
          >
            <g id="tx_applying_908ukv51hc7-fill" stroke="none" fill="#f4f4f4">
              <foreignObject x="12" y="10" width="200" height="60">
                <div
                  className="text-center"
                  style={{
                    font: "10px Roboto, sans-serif",
                    color: "#f4f4f4",
                    whiteSpace: "pre-wrap",
                    overflowWrap: "break-word",
                    maxWidth: "200px",
                    cursor: "pointer",
                    display:
                      editingField === "description" && editingStep === 1
                        ? "none"
                        : "block",
                  }}
                  onClick={() =>
                    startEditing("description", 1, content.steps[1].description)
                  }
                >
                  {content.steps[1].description}
                </div>
                {editingField === "description" && editingStep === 1 && (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingStep(null);
                      if (tempValue !== content.steps[1].description) {
                        handleStepUpdate(1, "description", tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    className="px-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary"
                    style={{
                      font: "10px Roboto, sans-serif",
                      maxWidth: "200px",
                    }}
                  />
                )}
              </foreignObject>
            </g>
          </g>

          {/* Step 3 Title and Description */}
          <g
            id="g-root-tx_achieveb_1uux9scv51erk-stroke-fill"
            data-item-order="0"
            transform="translate(245.5, 338)"
          >
            <g
              id="tx_achieveb_1uux9scv51erk-fill"
              stroke="none"
              fill={content.steps[2].color || "#a6da37"}
            >
              <foreignObject x="15.76" y="10" width="150" height="80">
                <div
                  className="text-center"
                  style={{
                    font: "bold 12px Roboto, sans-serif",
                    color: content.steps[2].color || "#a6da37",
                    whiteSpace: "pre-wrap",
                    cursor: "pointer",
                    display:
                      editingField === "title" && editingStep === 2
                        ? "none"
                        : "block",
                  }}
                  onClick={() =>
                    startEditing("title", 2, content.steps[2].title)
                  }
                >
                  {content.steps[2].title}
                </div>
                {editingField === "title" && editingStep === 2 && (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingStep(null);
                      if (tempValue !== content.steps[2].title) {
                        handleStepUpdate(2, "title", tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    className="px-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary"
                    style={{ font: "bold 16px Roboto, sans-serif" }}
                  />
                )}
              </foreignObject>
            </g>
          </g>
          <g
            id="g-root-tx_reaching_1q5qnd8v572tm-stroke-fill"
            data-item-order="0"
            transform="translate(255.5, 354)"
          >
            <g id="tx_reaching_1q5qnd8v572tm-fill" stroke="none" fill="#f4f4f4">
              <foreignObject x="12" y="10" width="150" height="60">
                <div
                  className="text-center"
                  style={{
                    font: "10px Roboto, sans-serif",
                    color: "#f4f4f4",
                    whiteSpace: "pre-wrap",
                    overflowWrap: "break-word",
                    maxWidth: "170px",
                    cursor: "pointer",
                    display:
                      editingField === "description" && editingStep === 2
                        ? "none"
                        : "block",
                  }}
                  onClick={() =>
                    startEditing("description", 2, content.steps[2].description)
                  }
                >
                  {content.steps[2].description}
                </div>
                {editingField === "description" && editingStep === 2 && (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingField(null);
                      setEditingStep(null);
                      if (tempValue !== content.steps[2].description) {
                        handleStepUpdate(2, "description", tempValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    className="px-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary"
                    style={{
                      font: "10px Roboto, sans-serif",
                      maxWidth: "200px",
                    }}
                  />
                )}
              </foreignObject>
            </g>
          </g>

          {/* Icons */}
          <g
            id="g-root-ic_path_8x5k24v4x7ur-stroke-fill"
            data-item-order="0"
            transform="translate(78, 160)"
          >
            <foreignObject x="0" y="0" width="40" height="40">
              <NetworkIcon
                value={content.steps[0].iconId || "git-branch"}
                type="LUCIDE"
                style={{
                  color: content.steps[0].color || "#1ac3fb",
                  width: "40px",
                  height: "40px",
                }}
              />
            </foreignObject>
          </g>
          <g
            id="g-root-ic_moni_vbqrpov4ymml-stroke-fill"
            data-item-order="0"
            transform="translate(118, 255)"
          >
            <foreignObject x="0" y="0" width="40" height="40">
              <NetworkIcon
                value={content.steps[1].iconId || "monitor"}
                type="LUCIDE"
                style={{
                  color: content.steps[1].color || "#43dd93",
                  width: "40px",
                  height: "40px",
                }}
              />
            </foreignObject>
          </g>
          <g
            id="g-root-ic_trop_1lza9jgv51fk9-stroke-fill"
            data-item-order="0"
            transform="translate(160, 350)"
          >
            <foreignObject x="0" y="0" width="40" height="40">
              <NetworkIcon
                value={content.steps[2].iconId || "trophy"}
                type="LUCIDE"
                style={{
                  color: content.steps[2].color || "#a6da37",
                  width: "40px",
                  height: "40px",
                }}
              />
            </foreignObject>
          </g>
        </svg>
      </motion.div>
    </motion.div>
  );
}
